import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { formatCurrency } from "@/lib/utils";
import { Shield } from "lucide-react";

const OPTION_STYLE = (selected) =>
  `w-full p-3 text-left border-2 rounded-lg transition-colors ${selected ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`;

export default function RiskSection({ decision, updateDecision }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Insurance & Reserve */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> {t("decisions.insuranceLevel")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <OptionGroup label={t("decisions.insuranceLevel")} field="insurance_level" decision={decision} updateDecision={updateDecision} options={[
              { value: "basic", label: "Basic", cost: "$100K/qtr" },
              { value: "standard", label: "Standard", cost: "$250K/qtr" },
              { value: "comprehensive", label: "Comprehensive", cost: "$400K/qtr" },
            ]} />
            <div>
              <div className="flex justify-between mb-1"><Label className="text-xs">{t("decisions.crisisReserve")}</Label><span className="font-mono text-xs text-sky-600">{formatCurrency(decision.crisis_reserve || 0)}</span></div>
              <Slider value={[decision.crisis_reserve || 0]} onValueChange={([v]) => updateDecision("crisis_reserve", v)} min={0} max={2000000} step={100000} className="mt-2" data-testid="crisis-reserve-slider" />
              <p className="text-[10px] text-slate-400 mt-1">Locked cash - available only during crisis events</p>
            </div>
            <OptionGroup label={t("decisions.strikeContingency")} field="strike_contingency" decision={decision} updateDecision={updateDecision} options={[
              { value: "none", label: "None", cost: "$0" },
              { value: "basic", label: "Basic", cost: "$50K/qtr" },
              { value: "full", label: "Full", cost: "$200K/qtr" },
            ]} />
          </CardContent>
        </Card>

        {/* Environmental */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.environmentalESG")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <OptionGroup label={t("decisions.environmentalCompliance")} field="environmental_compliance" decision={decision} updateDecision={updateDecision} options={[
              { value: "minimum", label: "Minimum", cost: "$0", desc: "Regulatory minimum only" },
              { value: "standard", label: "Standard", cost: "$100K/qtr", desc: "Above average compliance" },
              { value: "green_leader", label: "Green Leader", cost: "$350K/qtr", desc: "+brand awareness, ESG score" },
            ]} />
            <OptionGroup label={t("decisions.carbonOffset")} field="carbon_offset" decision={decision} updateDecision={updateDecision} options={[
              { value: "none", label: "None", cost: "$0" },
              { value: "partial", label: "Partial Offset", cost: "$75K/qtr" },
              { value: "full", label: "Full Offset", cost: "$200K/qtr" },
            ]} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OptionGroup({ label, field, decision, updateDecision, options }) {
  const current = decision[field] || options[0]?.value;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="space-y-1.5 mt-1.5">
        {options.map(opt => (
          <button key={opt.value} onClick={() => updateDecision(field, opt.value)} className={OPTION_STYLE(current === opt.value)} data-testid={`${field}-${opt.value}`}>
            <div className="flex justify-between"><span className="font-medium text-sm">{opt.label}</span><span className="text-xs font-mono text-slate-500">{opt.cost}</span></div>
            {opt.desc && <p className="text-xs text-slate-500">{opt.desc}</p>}
          </button>
        ))}
      </div>
    </div>
  );
}
